/**
 * 将完整的语言代码转换为短代码
 * @param {string} lang - 完整的语言代码（如 'zh_cn', 'en_us' 等）
 * @returns {string} 短语言代码（如 'zh', 'en' 等）
 */
function convertLangCode(lang) {
  if (!lang) return 'en';
  
  // 如果已经是短代码，直接返回
  if (lang.length <= 2) {
    return lang;
  }
  
  // 将完整的语言代码转换为短代码
  const langMap = {
    'zh_cn': 'zh',
    'en_us': 'en',
    'es_spa': 'es',
    'ms_my': 'ms',
    'ja_jp': 'ja',
    'ko_kr': 'ko',
    'de_de': 'de',
    'mn_mn': 'mn'
  };
  
  return langMap[lang] || lang.substring(0, 2);
}

/**
 * 将短代码转换为完整的语言代码
 * @param {string} shortCode - 短语言代码（如 'zh', 'en' 等）
 * @returns {string} 完整的语言代码（如 'zh_cn', 'en_us' 等）
 */
function convertToFullLangCode(shortCode) {
  if (!shortCode) return 'en_us';
  
  // 如果已经是完整代码，直接返回
  if (shortCode.includes('_')) {
    return shortCode;
  }
  
  // 将短代码转换为完整代码
  const langMap = {
    'zh': 'zh_cn',
    'en': 'en_us',
    'es': 'es_spa',
    'ms': 'ms_my',
    'ja': 'ja_jp',
    'ko': 'ko_kr',
    'de': 'de_de',
    'mn': 'mn_mn'
  };
  
  return langMap[shortCode] || 'en_us';
}

/**
 * 获取当前语言代码（短代码）
 * @returns {string} 当前语言代码（如 'zh', 'en' 等）
 */
export function getCurrentLang() {
  // 仅保留英语
  return "en";
}

/**
 * 获取当前完整语言代码
 * @returns {string} 当前完整语言代码（如 'zh_cn', 'en_us' 等）
 */
export function getCurrentFullLang() {
  // 仅保留英语
  return "en_us";
}

/**
 * 设置当前语言
 * @param {string} lang - 语言代码（可以是短代码或完整代码）
 * @returns {string} 标准化后的完整语言代码
 */
export function setCurrentLang() {
  // 仅保留英语
  localStorage.setItem("lang", "en_us");
  return "en_us";
}

/**
 * 同步语言到后端
 * @param {object} http - HTTP 实例
 * @param {string} lang - 语言代码
 */
export function syncLangWithBackend(http, lang) {
  if (!http || !lang) return;
  
  const fullLang = convertToFullLangCode(lang);
  
  // 如果用户已登录，同步语言设置到后端
  if (localStorage.getItem('token')) {
    try {
      http({
        method: 'post',
        url: 'update_lang',
        data: { lang: fullLang }
      }).catch(err => {
        console.warn('Failed to sync language to backend:', err);
      });
    } catch (err) {
      console.warn('Failed to sync language to backend:', err);
    }
  }
}

/**
 * 从 i18n locale 获取语言代码
 * @param {string} locale - i18n locale 值
 * @returns {string} 短语言代码
 */
export function getLangFromLocale(locale) {
  return convertLangCode(locale);
}

// 后端动态数据（国家、名称、标签等）的中文 -> 英文映射
const cnToEnDict = {
  // 国家/地区
  '中国': 'China',
  '中國': 'China',
  '韩国': 'Korea',
  '韓國': 'Korea',
  '日本': 'Japan',
  '美国': 'USA',
  '美國': 'USA',
  '英国': 'UK',
  '英國': 'UK',
  '法国': 'France',
  '法國': 'France',
  '德国': 'Germany',
  '德國': 'Germany',
  '俄罗斯': 'Russia',
  '俄羅斯': 'Russia',
  '泰国': 'Thailand',
  '泰國': 'Thailand',
  '越南': 'Vietnam',
  '印度': 'India',
  '印尼': 'Indonesia',
  '菲律宾': 'Philippines',
  '菲律賓': 'Philippines',
  '新加坡': 'Singapore',
  '马来西亚': 'Malaysia',
  '馬來西亞': 'Malaysia',
  '香港': 'Hong Kong',
  '澳门': 'Macau',
  '澳門': 'Macau',
  '台湾': 'Taiwan',
  '台灣': 'Taiwan',
  '澳大利亚': 'Australia',
  '澳大利亞': 'Australia',
  '加拿大': 'Canada',
  '巴西': 'Brazil',
  '蒙古': 'Mongolia',
  '西班牙': 'Spain',
  // 名称/常见词
  '美女': 'Beauty',
  '老师': 'Teacher',
  '老師': 'Teacher',
  '护士': 'Nurse',
  '護士': 'Nurse',
  '模特': 'Model',
  '学生': 'Student',
  '學生': 'Student',
  '空姐': 'Stewardess',
  '少妇': 'Young woman',
  '少婦': 'Young woman',
  '网红': 'Internet celebrity',
  '網紅': 'Internet celebrity',
  '主播': 'Host',
  '秘书': 'Secretary',
  '秘書': 'Secretary',
};

const cnKeysByLength = Object.keys(cnToEnDict).sort((a, b) => b.length - a.length);

/**
 * 将后端返回的中文数据翻译为英文（找不到则原样返回）
 * @param {string} text
 * @returns {string}
 */
export function translateCN(text) {
  if (text == null) return text;
  let s = String(text);
  if (cnToEnDict[s]) return cnToEnDict[s];
  cnKeysByLength.forEach((k) => {
    if (s.indexOf(k) !== -1) {
      s = s.split(k).join(cnToEnDict[k]);
    }
  });
  return s;
}
